/**
 * ASNPlusPage - Subscription landing page for ASN Plus ($3.99/mo)
 * Shows features, pricing, and handles Stripe checkout flow.
 */
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Crown, Zap, MonitorPlay, MessageSquare, BarChart3,
  Trophy, Users, Mail, Check, Loader2, ArrowLeft, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURES = [
  { icon: Zap, title: 'Ad-Free Viewing', desc: 'Watch every broadcast without any ad breaks or interruptions' },
  { icon: MonitorPlay, title: 'Multi-View', desc: 'Watch multiple live games simultaneously on one screen' },
  { icon: Trophy, title: "Pick'em Predictions", desc: 'Compete in weekly game prediction contests with leaderboards' },
  { icon: Users, title: 'Watch Party Hosting', desc: 'Create and host watch parties for live broadcasts' },
  { icon: MessageSquare, title: 'Exclusive Inbox', desc: 'Direct messaging channel with the ASN team' },
  { icon: Mail, title: 'Newsletters & Polls', desc: 'Receive exclusive newsletters and vote in subscriber-only polls' },
];

export const ASNPlusPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [subStatus, setSubStatus] = useState(null);
  const [checking, setChecking] = useState(true);
  const sessionId = searchParams.get('session_id');
  const isSuccess = window.location.pathname.includes('/plus/success');

  const userToken = localStorage.getItem('userToken');
  const userData = JSON.parse(localStorage.getItem('userData') || 'null');
  const isLoggedIn = !!userToken && !!userData;

  useEffect(() => {
    if (isLoggedIn) {
      checkStatus();
    } else {
      setChecking(false);
    }
  }, [isLoggedIn]);

  // Poll checkout status on success redirect
  useEffect(() => {
    if (isSuccess && sessionId && isLoggedIn) {
      pollCheckoutStatus(sessionId, 0);
    }
  }, [isSuccess, sessionId, isLoggedIn]);

  const checkStatus = async () => {
    try {
      const res = await axios.get(`${API}/subscriptions/status`, {
        headers: { Authorization: `Bearer ${userToken}` }
      });
      setSubStatus(res.data);
    } catch {}
    finally { setChecking(false); }
  };

  const pollCheckoutStatus = async (sid, attempt) => {
    if (attempt >= 8) return;
    try {
      const res = await axios.get(`${API}/subscriptions/checkout-status/${sid}`, {
        headers: { Authorization: `Bearer ${userToken}` }
      });
      if (res.data.payment_status === 'paid') {
        // Update local storage
        const ud = JSON.parse(localStorage.getItem('userData') || '{}');
        ud.is_plus = true;
        localStorage.setItem('userData', JSON.stringify(ud));
        setSubStatus({ is_plus: true, plan: 'asn_plus' });
        return;
      }
      if (res.data.status === 'expired') return;
      setTimeout(() => pollCheckoutStatus(sid, attempt + 1), 2500);
    } catch {
      setTimeout(() => pollCheckoutStatus(sid, attempt + 1), 2500);
    }
  };

  const handleSubscribe = async () => {
    if (!isLoggedIn) {
      navigate(`/login-user?return=/plus`);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/subscriptions/create-checkout`, {
        origin_url: window.location.origin
      }, { headers: { Authorization: `Bearer ${userToken}` } });
      window.location.href = res.data.url;
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to start checkout';
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel your ASN Plus subscription? You\'ll keep access until the end of your billing period.')) return;
    try {
      await axios.post(`${API}/subscriptions/cancel`, {}, {
        headers: { Authorization: `Bearer ${userToken}` }
      });
      checkStatus();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to cancel');
    }
  };

  const isPlus = subStatus?.is_plus;
  const isCanceling = subStatus?.subscription?.status === 'canceling';

  return (
    <main className="min-h-screen bg-[#050505]">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Back */}
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-zinc-500 hover:text-white text-sm mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        {/* Success Banner */}
        {isSuccess && isPlus && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-6 bg-gradient-to-r from-orange-500/20 via-orange-500/10 to-transparent border border-orange-500/30 rounded-lg text-center">
            <Check className="w-10 h-10 text-orange-400 mx-auto mb-3" />
            <h2 className="text-white text-xl font-bold mb-1">Welcome to ASN Plus!</h2>
            <p className="text-zinc-400 text-sm">Your subscription is now active. Enjoy all premium features.</p>
          </motion.div>
        )}

        {/* Hero */}
        <div className="text-center mb-12">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
            <img src="/asn-plus-logo.png" alt="ASN Plus" className="w-32 h-32 mx-auto mb-6 object-contain" data-testid="asn-plus-logo" />
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="font-secondary text-4xl sm:text-5xl font-bold uppercase tracking-tight text-white mb-3" data-testid="asn-plus-title">
            ASN Plus
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="text-zinc-400 text-base max-w-lg mx-auto">
            The premium experience for Antler Sports Network. Ad-free broadcasts, exclusive features, and direct access.
          </motion.p>
        </div>

        {/* Pricing Card */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="max-w-sm mx-auto mb-16 bg-zinc-900/80 border border-orange-500/20 rounded-lg overflow-hidden" data-testid="pricing-card">
          <div className="bg-gradient-to-r from-orange-600 to-orange-500 px-6 py-4 text-center">
            <Crown className="w-6 h-6 text-white mx-auto mb-1" />
            <span className="text-white text-xs font-bold uppercase tracking-widest">ASN Plus</span>
          </div>
          <div className="p-6 text-center">
            <div className="mb-4">
              <span className="text-white text-5xl font-bold">$3.99</span>
              <span className="text-zinc-400 text-base ml-1">/month</span>
            </div>
            <ul className="text-left space-y-2 mb-6">
              {FEATURES.map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-zinc-300">
                  <Check className="w-4 h-4 text-orange-400 flex-shrink-0" />
                  {f.title}
                </li>
              ))}
            </ul>

            {checking ? (
              <div className="flex justify-center py-3"><Loader2 className="w-5 h-5 animate-spin text-orange-500" /></div>
            ) : isPlus && !isCanceling ? (
              <div className="space-y-3">
                <div className="flex items-center justify-center gap-2 text-orange-400 font-bold text-sm">
                  <Crown className="w-4 h-4" /> Active Subscriber
                </div>
                <button onClick={handleCancel} className="text-zinc-600 hover:text-red-400 text-xs transition-colors" data-testid="cancel-sub-btn">
                  Cancel Subscription
                </button>
              </div>
            ) : isCanceling ? (
              <div className="text-zinc-500 text-sm">
                Subscription cancels at end of billing period
              </div>
            ) : (
              <Button onClick={handleSubscribe} disabled={loading}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 text-sm" data-testid="subscribe-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Crown className="w-4 h-4 mr-2" />}
                {isLoggedIn ? 'Subscribe Now' : 'Sign In to Subscribe'}
              </Button>
            )}
          </div>
        </motion.div>

        {/* Features Grid */}
        <div className="mb-16">
          <h2 className="font-secondary text-2xl font-bold uppercase tracking-tight text-white text-center mb-8">
            What You Get
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="bg-zinc-900/50 border border-white/5 hover:border-orange-500/20 rounded-lg p-5 transition-colors group"
                data-testid={`feature-card-${i}`}>
                <f.icon className="w-8 h-8 text-orange-500 mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="text-white font-bold text-sm mb-1">{f.title}</h3>
                <p className="text-zinc-500 text-xs leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="max-w-2xl mx-auto mb-16">
          <h2 className="font-secondary text-xl font-bold uppercase tracking-tight text-white text-center mb-6">
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {[
              { q: 'Can I still watch live broadcasts for free?', a: 'Yes! All live broadcasts remain free to watch. ASN Plus enhances your experience with ad-free viewing and exclusive features.' },
              { q: 'Can I cancel anytime?', a: 'Absolutely. Cancel anytime from your account page. You\'ll keep access until the end of your billing period.' },
              { q: 'What payment methods are accepted?', a: 'We accept all major credit and debit cards through our secure Stripe payment processor.' },
            ].map((faq, i) => (
              <div key={i} className="bg-zinc-900/30 border border-white/5 rounded-lg p-4">
                <h4 className="text-white text-sm font-bold mb-1">{faq.q}</h4>
                <p className="text-zinc-500 text-xs">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
};

export default ASNPlusPage;
